import type { GeoLocationSnapshot, LocationSource } from '@/core/geolocation/locationRetry'
import { type GPSResult, GPS_ACQUIRE_GATE_TIMEOUT, getGPS } from './useStampFlowStateMachine'

/** getGPS() の結果を postWithLocationRetry が受け取る最小形に落とす */
export const toLocationSnapshot = (result: GPSResult): GeoLocationSnapshot => ({
  lat: result.lat,
  lon: result.lon,
  available: result.available,
})

/**
 * AR フォト・音声ガイド押下時の測位元を作る（#3727）。
 *
 * - available を常に false にし、入場時・獲得時に取った座標（stamp.context.gps）は使わない。
 *   ページを開いてから移動した利用者が、古い座標で範囲外と判定されるのを防ぐ
 * - サーバーが location_required を返したときだけ requestLocation() が呼ばれ、押下のたびに測位し直す
 *   （useGeoLocation.requestLocation のように取得済み座標をキャッシュしない）
 * - 打ち切りは獲得押下時と同じ GPS_ACQUIRE_GATE_TIMEOUT（15 秒）。利用者が押して待つ意図がある点が同じで、
 *   iOS のコールドフィックスに 10 秒では間に合わなかった（#3541）
 *
 * locate を注入できるようにしているのはテストのため（navigator.geolocation をモックしなくてよい）。
 */
export const createTapLocationSource = (
  locate: () => Promise<GPSResult> = () => getGPS(GPS_ACQUIRE_GATE_TIMEOUT),
): LocationSource => {
  // AR フォトと音声ガイドを続けて押しても watchPosition を 2 本走らせない（useGeoLocation.startRequest と同じ）。
  // 結果は保持せず、完了したら次の押下で測位し直す
  let inFlight: Promise<GeoLocationSnapshot> | null = null
  return {
    available: false,
    lat: 0,
    lon: 0,
    requestLocation: () => {
      if (inFlight) return inFlight
      inFlight = locate()
        .then(toLocationSnapshot)
        .finally(() => {
          inFlight = null
        })
      return inFlight
    },
  }
}

/** detail.tsx で使う既定の測位元。測位中の Promise 以外の状態（座標など）を持たないのでモジュール定数でよい */
export const TAP_LOCATION_SOURCE: LocationSource = createTapLocationSource()
